import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Ban, Send } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Auction, Bid, ExternalPlayer, Message, PlayerNews, PlayerSeasonStats, Profile } from '../types';
import { BidPanel } from '../components/BidPanel';
import { ChatBox } from '../components/ChatBox';
import { Timer } from '../components/Timer';
import { formatCurrency, formatDateTime, participantLabel } from '../lib/format';
import { useAuth } from '../contexts/AuthContext';
import { PlayerInfo, PlayerNewsPanel } from '../components/PlayerInfo';

function getAuctionImagePath(imageUrl: string | null): string | null {
  if (!imageUrl) return null;
  const marker = '/auction-images/';
  const index = imageUrl.indexOf(marker);
  if (index === -1) return null;
  const rawPath = imageUrl.slice(index + marker.length).split('?')[0];
  try {
    return decodeURIComponent(rawPath);
  } catch {
    return rawPath;
  }
}

export default function AuctionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [auction, setAuction] = useState<Auction | null>(null);
  const [bids, setBids] = useState<Bid[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [winner, setWinner] = useState<Profile | null>(null);
  const [creator, setCreator] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [player, setPlayer] = useState<ExternalPlayer | null>(null);
  const [playerStats, setPlayerStats] = useState<PlayerSeasonStats | null>(null);
  const [playerNews, setPlayerNews] = useState<PlayerNews[]>([]);

  const loadAuction = async () => {
    if (!id) return;
    const { data, error } = await supabase.from('auctions').select('*').eq('id', id).single();
    if (error) console.error(error);
    const nextAuction = data as Auction | null;
    setAuction(nextAuction);

    if (nextAuction?.external_player_id) {
      const playerId = nextAuction.external_player_id;

      // Legge prima il giocatore: la Function viene invocata solo quando almeno
      // uno tra profilo, statistiche, valore o notizie è scaduto.
      const { data: cachedPlayerData, error: cachedPlayerError } = await supabase
        .from('external_players')
        .select('*')
        .eq('id', playerId)
        .maybeSingle();

      if (cachedPlayerError) console.error('Errore caricamento giocatore:', cachedPlayerError);

      const cachedPlayer = cachedPlayerData as ExternalPlayer | null;
      const now = Date.now();
      const isStale = (value: string | null | undefined, maxAgeMs: number) => {
        if (!value) return true;
        const timestamp = new Date(value).getTime();
        return Number.isNaN(timestamp) || now - timestamp >= maxAgeMs;
      };

      const shouldUpdatePlayerData = !cachedPlayer
        || isStale(cachedPlayer.news_updated_at, 2 * 60 * 60 * 1000)
        || isStale(cachedPlayer.stats_updated_at, 24 * 60 * 60 * 1000)
        || isStale(cachedPlayer.profile_updated_at, 7 * 24 * 60 * 60 * 1000)
        || isStale(cachedPlayer.market_value_updated_at, 7 * 24 * 60 * 60 * 1000);

      if (shouldUpdatePlayerData) {
        const { error: updateError } = await supabase.functions.invoke('update-player-data', {
          body: { player_id: playerId },
        });
        if (updateError) console.warn('Aggiornamento dati giocatore non riuscito:', updateError.message);
      }

      const [
        { data: playerData, error: playerError },
        { data: statsData, error: statsError },
        { data: newsData, error: newsError },
      ] = await Promise.all([
        supabase.from('external_players').select('*').eq('id', playerId).maybeSingle(),
        supabase.from('player_season_stats').select('*').eq('player_id', playerId).order('season', { ascending: false }).order('appearances', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('player_news').select('*').eq('player_id', playerId).gte('published_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()).order('published_at', { ascending: false }).limit(5),
      ]);

      if (playerError) console.error('Errore giocatore:', playerError);
      if (statsError) console.error('Errore statistiche:', statsError);
      if (newsError) console.error('Errore notizie:', newsError);

      setPlayer(playerData as ExternalPlayer | null);
      setPlayerStats(statsData as PlayerSeasonStats | null);
      setPlayerNews((newsData || []) as PlayerNews[]);
    } else {
      setPlayer(null);
      setPlayerStats(null);
      setPlayerNews([]);
    }

    if (nextAuction?.created_by) {
      const { data: creatorData } = await supabase.from('profiles').select('*').eq('id', nextAuction.created_by).maybeSingle();
      setCreator(creatorData as Profile | null);
    }

    if (nextAuction?.current_winner_id) {
      const { data: profileData } = await supabase.from('profiles').select('*').eq('id', nextAuction.current_winner_id).maybeSingle();
      setWinner(profileData as Profile | null);
    } else {
      setWinner(null);
    }
  };

  const loadBids = async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from('bids')
      .select('*, profiles(display_name,nickname,avatar_url)')
      .eq('auction_id', id)
      .order('created_at', { ascending: false })
      .order('amount', { ascending: false });
    if (error) console.error(error);
    setBids((data || []) as Bid[]);
  };

  const loadMessages = async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from('messages')
      .select('*, profiles(display_name,nickname,avatar_url)')
      .eq('auction_id', id)
      .order('created_at', { ascending: true });
    if (error) console.error(error);
    setMessages((data || []) as Message[]);
  };

  useEffect(() => {
    if (!id || !user) return;

    const init = async () => {
      setLoading(true);
      await supabase.from('auction_participants').upsert({ auction_id: id, user_id: user.id });
      await Promise.all([loadAuction(), loadBids(), loadMessages()]);
      setLoading(false);
    };
    init();

    const channel = supabase
      .channel(`auction-${id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'auctions', filter: `id=eq.${id}` }, loadAuction)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bids', filter: `auction_id=eq.${id}` }, () => { loadBids(); loadAuction(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages', filter: `auction_id=eq.${id}` }, loadMessages)
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [id, user]);

  const cancelAuction = async () => {
    if (!auction) return;
    const confirmed = window.confirm(`Vuoi davvero annullare l’asta “${auction.title}”? Verrà eliminata definitivamente e non comparirà nelle aste chiuse.`);
    if (!confirmed) return;

    setCancelling(true);
    setActionError(null);
    try {
      const imagePath = getAuctionImagePath(auction.image_url);
      const { error } = await supabase.rpc('cancel_auction', { p_auction_id: auction.id });
      if (error) throw error;

      // La RPC prova già a pulire Storage. Questo è un secondo tentativo lato client,
      // utile perché il creatore è anche owner del file nel bucket.
      if (imagePath) {
        const { error: removeError } = await supabase.storage.from('auction-images').remove([imagePath]);
        if (removeError) console.warn('Foto asta non eliminata dal bucket:', removeError.message);
      }

      navigate('/');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Annullamento non riuscito.');
    } finally {
      setCancelling(false);
    }
  };

  const sendCustomNotification = async () => {
    if (!auction || auction.created_by !== user?.id) return;
    const message = window.prompt('Scrivi il messaggio da inviare a tutti gli iscritti (massimo 300 caratteri):');
    if (message === null) return;
    const clean = message.trim();
    if (!clean) {
      setActionError('Inserisci un messaggio.');
      return;
    }

    setSendingNotification(true);
    setActionError(null);
    try {
      const { data, error } = await supabase.rpc('send_auction_custom_notification', {
        p_auction_id: auction.id,
        p_message: clean
      });
      if (error) throw error;
      window.alert(`Notifica inviata a ${Number(data) || 0} iscritti.`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Invio notifica non riuscito.');
    } finally {
      setSendingNotification(false);
    }
  };

  if (loading) return <div className="screen-center small">Caricamento asta…</div>;
  if (!auction) return <div className="panel">Asta non trovata.</div>;

  const chatLocked = auction.status !== 'open' && auction.chat_locked_on_close;

  return (
    <div className="detail-layout">
      <section className="panel auction-detail-hero">
        {auction.image_url ? <img src={auction.image_url} alt="" className="detail-image" /> : <div className="detail-placeholder">🏷️</div>}
        <div className="detail-info">
          <div className="detail-title-row">
            <div>
              <span className={auction.status === 'open' ? 'status-pill open static' : 'status-pill closed static'}>{auction.status === 'open' ? 'Aperta' : 'Chiusa'}</span>
              <h1>{auction.title}</h1>
            </div>
            <div className="detail-actions">
              {auction.created_by === user?.id && (
                <>
                  <button className="ghost-button" onClick={sendCustomNotification} disabled={sendingNotification}>
                    <Send size={18} /> {sendingNotification ? 'Invio…' : 'Invia notifica'}
                  </button>
                  {auction.status === 'open' && (
                    <button className="danger-button" onClick={cancelAuction} disabled={cancelling}>
                      <Ban size={18} /> {cancelling ? 'Annullamento…' : 'Annulla asta'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          <p className="auction-owner detail-owner">Messo all’asta da <strong>{participantLabel(creator)}</strong></p>
          <p>{auction.description}</p>
          <div className="anti-sniping-summary" aria-label="Impostazioni prolungamento asta">
            <span>Prolungamento timer</span>
            <strong>
              {auction.auto_extend_enabled && auction.anti_sniping_window_minutes > 0
                ? `Offerta negli ultimi ${auction.anti_sniping_window_minutes} min: +${auction.extension_minutes} min`
                : 'Disattivato'}
            </strong>
          </div>
          <Timer closesAt={auction.closes_at} status={auction.status} large />
          <div className="detail-stats">
            <div><span>Base d’asta</span><strong>{formatCurrency(auction.starting_price)}</strong></div>
            <div><span>{auction.bid_count > 0 ? 'Offerta attuale' : 'Offerta'}</span><strong>{auction.bid_count > 0 ? formatCurrency(auction.current_amount) : 'Nessuna offerta'}</strong></div>
            <div><span>Vincitore</span><strong>{winner ? participantLabel(winner) : 'Nessun vincitore'}</strong></div>
            <div><span>Rilanci</span><strong>{auction.bid_count}</strong></div>
          </div>
          {actionError && <p className="error-text">{actionError}</p>}
        </div>
      </section>

      {player && <PlayerInfo player={player} stats={playerStats} />}

      <div className="detail-columns">
        <div className="left-column">
          <BidPanel auction={auction} onBid={() => { loadAuction(); loadBids(); }} />
          <section className="panel">
            <div className="panel-header compact"><h2>Storico offerte</h2></div>
            <div className="bid-history">
              {bids.map((bid) => (
                <article key={bid.id} className="bid-row">
                  <div>
                    <strong>{participantLabel(bid.profiles)}</strong>
                    <span>{formatDateTime(bid.created_at)}</span>
                  </div>
                  <strong>{formatCurrency(bid.amount)}</strong>
                </article>
              ))}
              {bids.length === 0 && <p className="muted centered">Ancora nessuna offerta.</p>}
            </div>
          </section>
        </div>
        <ChatBox auctionId={auction.id} messages={messages} locked={chatLocked} />
      </div>

      {player && <PlayerNewsPanel news={playerNews} />}

      <Link className="back-link" to="/">← Torna alla home</Link>
    </div>
  );
}
